/**
 * Method Execution Service for dev-tools
 * Part of Phase 2 refactoring - Service Layer (Business Logic)
 *
 * Handles method execution and API calls
 */
import { supportServiceConnectClient } from "api";

import { AVAILABLE_SERVICES } from "../constants";
import { MethodInvocationResult } from "../types";
import { logger } from "../utils/logger";

// Map of service clients
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Service clients have different types from different protobuf packages
const SERVICE_CLIENTS: Record<string, any> = {
  support: supportServiceConnectClient,
};

export class MethodExecutionService {
  /**
   * Execute a method with the provided form data for a specific service
   */
  static async executeMethodForService(
    serviceId: string,
    methodName: string,
    formData: Record<string, unknown>
  ): Promise<MethodInvocationResult> {
    const startTime = Date.now();

    try {
      // Find service definition
      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition) {
        throw new Error(`Service ${serviceId} not found`);
      }

      // Get service client
      const serviceClient = SERVICE_CLIENTS[serviceId];
      if (!serviceClient) {
        throw new Error(`Service client for ${serviceId} not available`);
      }

      // Validate method exists
      const method = serviceDefinition.service.methods[methodName];
      if (!method?.I) {
        throw new Error(
          `Method ${methodName} not found in ${serviceId} service or has no input type`
        );
      }

      // Create request object
      const request = new method.I(formData);

      // Execute method
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic method invocation requires any type for service client methods
      const response = await (serviceClient as any)[methodName](request);

      return {
        success: true,
        data: response,
        duration: Date.now() - startTime,
        timestamp: new Date(),
        methodName,
        requestData: formData,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
        duration: Date.now() - startTime,
        timestamp: new Date(),
        methodName,
        requestData: formData,
      };
    }
  }

  /**
   * Execute a method with the provided form data (legacy support service method)
   */
  static async executeMethod(
    methodName: string,
    formData: Record<string, unknown>
  ): Promise<MethodInvocationResult> {
    return this.executeMethodForService("support", methodName, formData);
  }

  /**
   * Validate if a method exists in a specific service
   */
  static validateMethodExistsForService(
    serviceId: string,
    methodName: string
  ): boolean {
    const serviceDefinition = AVAILABLE_SERVICES.find(
      (s) => s.id === serviceId
    );
    if (!serviceDefinition || !serviceDefinition.service?.methods) return false;
    return methodName in serviceDefinition.service.methods;
  }

  /**
   * Validate if a method exists in the support service (legacy method)
   */
  static validateMethodExists(methodName: string): boolean {
    return this.validateMethodExistsForService("support", methodName);
  }

  /**
   * Get the input type constructor for a method in a specific service
   */
  static getMethodInputTypeForService(
    serviceId: string,
    methodName: string
  ): unknown {
    const serviceDefinition = AVAILABLE_SERVICES.find(
      (s) => s.id === serviceId
    );
    if (!serviceDefinition) return null;
    const method = serviceDefinition.service.methods[methodName];
    return method?.I || null;
  }

  /**
   * Get the output type constructor for a method in a specific service
   */
  static getMethodOutputTypeForService(
    serviceId: string,
    methodName: string
  ): unknown {
    const serviceDefinition = AVAILABLE_SERVICES.find(
      (s) => s.id === serviceId
    );
    if (!serviceDefinition) return null;
    const method = serviceDefinition.service.methods[methodName];
    return method?.O || null;
  }

  /**
   * Get all available method names for a specific service
   */
  static getAvailableMethodNamesForService(serviceId: string): string[] {
    const serviceDefinition = AVAILABLE_SERVICES.find(
      (s) => s.id === serviceId
    );
    if (!serviceDefinition) return [];
    return Object.keys(serviceDefinition.service.methods);
  }

  /**
   * Get all available method names (legacy support service method)
   */
  static getAvailableMethodNames(): string[] {
    return this.getAvailableMethodNamesForService("support");
  }

  /**
   * Validate form data against method input schema for a specific service
   */
  static validateInputDataForService(
    serviceId: string,
    methodName: string,
    formData: Record<string, unknown>
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    try {
      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition) {
        errors.push(`Service ${serviceId} not found`);
        return { valid: false, errors };
      }

      const method = serviceDefinition.service.methods[methodName];
      if (!method?.I) {
        errors.push(
          `Method ${methodName} not found in ${serviceId} service or has no input type`
        );
        return { valid: false, errors };
      }

      // Create a test request to validate the data structure
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Variable used for validation side effects, constructor throws on invalid data
      const request = new method.I(formData);

      // If we get here without throwing, the data is structurally valid
      return { valid: true, errors: [] };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown validation error";
      errors.push(errorMessage);
      return { valid: false, errors };
    }
  }

  /**
   * Validate form data against method input schema (legacy support service method)
   */
  static validateInputData(
    methodName: string,
    formData: Record<string, unknown>
  ): { valid: boolean; errors: string[] } {
    return this.validateInputDataForService("support", methodName, formData);
  }

  /**
   * Get method metadata including input/output types for a specific service
   */
  static getMethodMetadataForService(
    serviceId: string,
    methodName: string
  ): {
    exists: boolean;
    hasInput: boolean;
    hasOutput: boolean;
    inputType?: unknown;
    outputType?: unknown;
  } {
    const serviceDefinition = AVAILABLE_SERVICES.find(
      (s) => s.id === serviceId
    );
    if (!serviceDefinition) {
      return {
        exists: false,
        hasInput: false,
        hasOutput: false,
      };
    }

    const method = serviceDefinition.service.methods[methodName];

    return {
      exists: !!method,
      hasInput: !!method?.I,
      hasOutput: !!method?.O,
      inputType: method?.I,
      outputType: method?.O,
    };
  }

  /**
   * Get method metadata including input/output types (legacy support service method)
   */
  static getMethodMetadata(methodName: string): {
    exists: boolean;
    hasInput: boolean;
    hasOutput: boolean;
    inputType?: unknown;
    outputType?: unknown;
  } {
    return this.getMethodMetadataForService("support", methodName);
  }

  /**
   * Create a sample request object for testing for a specific service
   */
  static createSampleRequestForService(
    serviceId: string,
    methodName: string
  ): unknown {
    try {
      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition) return null;

      const method = serviceDefinition.service.methods[methodName];
      if (!method?.I) {
        return null;
      }

      // Create an empty request object to inspect its structure
      return new method.I({});
    } catch (error) {
      logger.error(
        `Failed to create sample request for ${methodName} in ${serviceId} service`,
        { error }
      );
      return null;
    }
  }

  /**
   * Create a sample request object for testing (legacy support service method)
   */
  static createSampleRequest(methodName: string): unknown {
    return this.createSampleRequestForService("support", methodName);
  }

  /**
   * Execute method with timeout
   */
  static async executeMethodWithTimeout(
    methodName: string,
    formData: Record<string, unknown>,
    timeoutMs = 30000
  ): Promise<MethodInvocationResult> {
    const timeoutPromise = new Promise<MethodInvocationResult>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Method execution timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    const executionPromise = this.executeMethod(methodName, formData);

    try {
      return await Promise.race([executionPromise, timeoutPromise]);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Execution timeout",
        duration: timeoutMs,
        timestamp: new Date(),
        methodName,
        requestData: formData,
      };
    }
  }
}
